/**
 * Walled books (owner, 29 Oct 2026: "all fab ev will go to sherry for approval
 * and he is the head admin of fab ev everything operates under him … make sure
 * no data leaks from on wheels to fab ev or fab ev to on wheels they are
 * seprate companies").
 *
 * A walled book is its own company. Nobody outside it reads or answers
 * anything in it, and nobody inside it reads or answers anything outside it.
 * The owner's head-admin desks alone see across the wall. Every approval in a
 * walled book goes to its head, beside the owner's desks.
 *
 * The maker's work for a walled book stays the maker's: it still builds and
 * dispatches FAB EV's orders and answers its service calls (deskScope), the
 * way a supplier does. That is supply, not FAB EV's office.
 */
import { isHeadAdminDesk, ORG_IDS } from './domain.js';

export const WALLED_BOOKS: readonly string[] = [ORG_IDS.fabev];

/** Each walled book's head admin: every approval in that book is theirs. */
export const BOOK_HEAD_EMAILS: Readonly<Record<string, readonly string[]>> = {
  [ORG_IDS.fabev]: ['sherryfabev@fabev.in'],
};

interface Who {
  email: string;
  organizationId: string;
}

const emailOf = (user: { email: string }) => user.email.trim().toLowerCase();

export function isWalledBook(organizationId: string): boolean {
  return WALLED_BOOKS.includes(organizationId);
}

/** The book this person heads, if any — only ever the book they sit in. */
export function bookHeadOf(user: Who | null | undefined): string | null {
  if (!user) return null;
  return (BOOK_HEAD_EMAILS[user.organizationId] ?? []).includes(emailOf(user)) ? user.organizationId : null;
}

export function isBookHead(user: Who | null | undefined): boolean {
  return bookHeadOf(user) !== null;
}

/**
 * Whether this person may reach a book's data at all. The owner's desks reach
 * every book; anyone else never crosses a wall, in either direction.
 */
export function reachesBook(user: Who, book: string): boolean {
  if (book === user.organizationId || isHeadAdminDesk(user)) return true;
  return !isWalledBook(book) && !isWalledBook(user.organizationId);
}

/** The books of a list this person may reach. */
export function wallBooks<T extends string>(user: Who, books: readonly T[]): T[] {
  return books.filter((book) => reachesBook(user, book));
}

/**
 * Whether this person answers approvals in a book: in a walled book its head
 * and the owner's desks only; elsewhere whoever may reach it (each kind's own
 * rule still applies on top).
 */
export function answersInBook(user: Who, book: string): boolean {
  if (!isWalledBook(book)) return reachesBook(user, book);
  return isHeadAdminDesk(user) || bookHeadOf(user) === book;
}

/** The books of a list whose approvals this person answers. */
export function answerBooks<T extends string>(user: Who, books: readonly T[]): T[] {
  return books.filter((book) => answersInBook(user, book));
}

/**
 * Who approves employees — a new enrolment and an Aadhaar check: the Bright
 * Autozone admin (owner, 19 Oct 2026: "employee aproval will come to
 * brightautozone admin only"), and a walled book's head for that book's own
 * people (29 Oct 2026).
 */
export const EMPLOYEE_APPROVER_EMAILS: readonly string[] = ['admin@brightautozone.in'];
export function approvesEmployees(user: { email: string; organizationId?: string } | null | undefined): boolean {
  if (!user) return false;
  if (EMPLOYEE_APPROVER_EMAILS.includes(emailOf(user))) return true;
  return user.organizationId !== undefined && isBookHead({ email: user.email, organizationId: user.organizationId });
}

/**
 * The books whose sold vehicles may come back into their own store (owner, 29 Oct 2026: "add a
 * return to fab ev store button on sold vehicles as well"), and who takes one back: the owner's
 * desks and the book's own head.
 */
export const SOLD_RETURN_BOOKS: readonly string[] = [ORG_IDS.fabev];
export function returnsSold(user: Who | null | undefined, book: string): boolean {
  if (!user || !SOLD_RETURN_BOOKS.includes(book)) return false;
  return isHeadAdminDesk(user) || bookHeadOf(user) === book;
}
